/**
 * usePopulationSegmentInteractivity — V1.0
 *
 * Hook de interactividad para segmentos de red en modo población por segmento.
 *
 * Activo cuando populationModeActive === true (independiente de isNetworkInteractive).
 * Cubre el caso principal: analizar → población activa, pero sin modo edición.
 *
 * Features:
 * - Hover con bounding-box (8px radius) para mejor hit detection en líneas finas
 * - Feature-state `hover: true` para feedback visual en el mapa
 * - Cursor `pointer` al pasar sobre segmentos
 * - Click con bbox → setSelectedEdgeId → abre EdgeDetailsPanel con tab 'population'
 * - Cleanup completo al desactivarse
 */

import { useEffect, useRef } from 'react';
import type { MapMouseEvent } from 'maplibre-gl';
import { mapEngine } from '../../map-engine/MapEngineService';
import { usePopulationSegmentSafe } from '../contexts/PopulationSegmentContext';

// ============================================================================
// CONSTANTS
// ============================================================================

const EDGES_LAYER_ID = 'canonical-network-edges';
const SOURCE_ID = 'canonical-network';

/**
 * Radio del bounding-box para queryRenderedFeatures.
 * 8px cubre la variación natural del puntero sobre líneas de 1-4px de ancho.
 */
const HIT_RADIUS = 8;

// ============================================================================
// TYPES
// ============================================================================

interface UsePopulationSegmentInteractivityOptions {
  /**
   * Función para seleccionar un edge en NetworkEditContext.
   * Dispara la apertura del EdgeDetailsPanel con tab 'population'.
   */
  setSelectedEdgeId: (id: string | null) => void;

  /**
   * Función opcional para sincronizar la selección en UrbanNetworkContext.
   * Actualiza el highlight visual (feature-state `selected`) en el mapa.
   */
  setUrbanNetworkSelectedEdgeId?: (id: string | null) => void;

  /**
   * Edge actualmente seleccionado. Se usa para limpiar la selección
   * cuando el usuario hace click en mapa vacío (sin edge).
   */
  selectedEdgeId?: string | null;
}

// ============================================================================
// HOOK
// ============================================================================

/**
 * Registra handlers de interactividad sobre la capa de red canónica
 * cuando el modo de población por segmento está activo.
 *
 * Llamar desde MapLayout junto a usePopulationVisualization().
 * El hook es no-op si PopulationSegmentProvider no está presente.
 */
export function usePopulationSegmentInteractivity({
  setSelectedEdgeId,
  setUrbanNetworkSelectedEdgeId,
  selectedEdgeId,
}: UsePopulationSegmentInteractivityOptions): void {
  const ctx = usePopulationSegmentSafe();
  const populationModeActive = ctx?.populationModeActive ?? false;

  // PERF: Refs para callbacks — evitan re-registro de listeners cuando
  // las callbacks cambian de referencia entre renders (mismo patrón que
  // useNetworkEditInteractivity).
  const setSelectedEdgeIdRef = useRef(setSelectedEdgeId);
  const setUrbanNetworkSelectedEdgeIdRef = useRef(setUrbanNetworkSelectedEdgeId);
  const selectedEdgeIdRef = useRef(selectedEdgeId);

  // Sync sin useEffect para sincronización inmediata en cada render
  useEffect(() => {
    setSelectedEdgeIdRef.current = setSelectedEdgeId;
    setUrbanNetworkSelectedEdgeIdRef.current = setUrbanNetworkSelectedEdgeId;
    selectedEdgeIdRef.current = selectedEdgeId;
  });

  // Trackeo del edge con hover activo (para limpieza precisa)
  const prevHoveredEdgeIdRef = useRef<string | null>(null);

  useEffect(() => {
    // ── Cleanup al desactivar ──
    if (!populationModeActive) {
      const map = mapEngine.getMap();
      if (map && prevHoveredEdgeIdRef.current) {
        try {
          map.removeFeatureState(
            { source: SOURCE_ID, id: prevHoveredEdgeIdRef.current },
            'hover',
          );
        } catch {
          // edge ya no en source — ignorar
        }
        map.getCanvas().style.cursor = '';
        prevHoveredEdgeIdRef.current = null;
      }
      return;
    }

    const map = mapEngine.getMap();
    if (!map) return;

    // ── Handler: mouse move sobre el mapa ──
    const handleMouseMove = (e: MapMouseEvent): void => {
      const bbox: [[number, number], [number, number]] = [
        [e.point.x - HIT_RADIUS, e.point.y - HIT_RADIUS],
        [e.point.x + HIT_RADIUS, e.point.y + HIT_RADIUS],
      ];

      const features = map.queryRenderedFeatures(bbox, { layers: [EDGES_LAYER_ID] });

      if (!features || features.length === 0) {
        // No hay edge bajo el cursor — limpiar hover previo
        if (prevHoveredEdgeIdRef.current) {
          try {
            map.removeFeatureState(
              { source: SOURCE_ID, id: prevHoveredEdgeIdRef.current },
              'hover',
            );
          } catch {}
          prevHoveredEdgeIdRef.current = null;
          map.getCanvas().style.cursor = '';
        }
        return;
      }

      const edgeId = features[0].id as string;
      if (!edgeId) return;

      // Solo actualizar si cambió el edge con hover
      if (prevHoveredEdgeIdRef.current !== edgeId) {
        // Limpiar hover anterior
        if (prevHoveredEdgeIdRef.current) {
          try {
            map.removeFeatureState(
              { source: SOURCE_ID, id: prevHoveredEdgeIdRef.current },
              'hover',
            );
          } catch {}
        }

        // Aplicar hover al edge actual
        try {
          map.setFeatureState({ source: SOURCE_ID, id: edgeId }, { hover: true });
        } catch {}

        prevHoveredEdgeIdRef.current = edgeId;
        map.getCanvas().style.cursor = 'pointer';
      }
    };

    // ── Handler: click en el mapa ──
    const handleClick = (e: MapMouseEvent): void => {
      // Solo left click
      if (e.originalEvent.button !== 0) return;

      const bbox: [[number, number], [number, number]] = [
        [e.point.x - HIT_RADIUS, e.point.y - HIT_RADIUS],
        [e.point.x + HIT_RADIUS, e.point.y + HIT_RADIUS],
      ];

      const features = map.queryRenderedFeatures(bbox, { layers: [EDGES_LAYER_ID] });

      if (!features || features.length === 0) {
        // Click en mapa vacío — limpiar selección si hay alguna activa
        if (selectedEdgeIdRef.current) {
          setSelectedEdgeIdRef.current(null);
          setUrbanNetworkSelectedEdgeIdRef.current?.(null);
        }
        return;
      }

      const edgeId = features[0].id as string;
      if (!edgeId) return;

      // Seleccionar edge — dispara apertura del EdgeDetailsPanel con tab 'population'
      setSelectedEdgeIdRef.current(edgeId);
      setUrbanNetworkSelectedEdgeIdRef.current?.(edgeId);

      console.log('[usePopulationSegmentInteractivity] Edge selected via population click:', edgeId);
    };

    // ── Handler: mouse abandona el canvas ──
    const handleMouseLeave = (): void => {
      if (prevHoveredEdgeIdRef.current) {
        try {
          map.removeFeatureState(
            { source: SOURCE_ID, id: prevHoveredEdgeIdRef.current },
            'hover',
          );
        } catch {}
        prevHoveredEdgeIdRef.current = null;
        map.getCanvas().style.cursor = '';
      }
    };

    // ── Registrar listeners ──
    map.on('mousemove', handleMouseMove);
    map.on('click', handleClick);
    map.on('mouseleave', handleMouseLeave);

    console.log('[usePopulationSegmentInteractivity] Listeners registered (populationModeActive=true)');

    // ── Cleanup al desmontar o desactivar ──
    return () => {
      map.off('mousemove', handleMouseMove);
      map.off('click', handleClick);
      map.off('mouseleave', handleMouseLeave);

      if (prevHoveredEdgeIdRef.current) {
        try {
          map.removeFeatureState(
            { source: SOURCE_ID, id: prevHoveredEdgeIdRef.current },
            'hover',
          );
        } catch {}
        prevHoveredEdgeIdRef.current = null;
        map.getCanvas().style.cursor = '';
      }

      console.log('[usePopulationSegmentInteractivity] Listeners removed');
    };
  }, [populationModeActive]);
}
